import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Button, Divider, Rating, Avatar, Stack, CircularProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { startChat } from '../../../actions/chat';
import { useNavigate } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { urlImg } from '../../common/helpers';

const PAGE_SIZE = 5;

const PageServiceList = ({ services, ownerName, irA, ownerId, categories, ownerAvatar, isOwner, pageId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  const isAuthenticated = useSelector(state => state.user.loading === false && state.user.user._id);

  const handleNavigate = (service) => {
    const userId = service.user || service.userId || ownerId;
    irA(`/servicio/${userId}/${service._id}`);
  };

  const handleQuote = () => {
    console.log('--- Debug Cotizar ---');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('ownerId:', ownerId);
    console.log('isOwner:', isOwner);
    console.log('pageId:', pageId);

    if (!isAuthenticated) {
      console.warn('Usuario no autenticado, redirigiendo...');
      navigate('/');
      return;
    }

    if (ownerId && !isOwner) {
      console.log('Iniciando chat...');
      const initialMsg = `Hola, me interesa contratar un servicio personalizado. Vi tu perfil de "${ownerName}" y me gustaría cotizar. ¿Podrías darme un presupuesto?`;
      dispatch(startChat(ownerId, pageId, 'page', initialMsg, `${ownerId}_${pageId}`));
    } else {
      console.warn('No se puede cotizar: ownerId faltante o es el dueño');
    }
  };

  const onlineUsers = useSelector(state => state.chat.onlineUsers);
  const isOnline = ownerId && onlineUsers.includes(ownerId);

  const visibleServices = (services || []).slice(0, visibleCount);
  const hasMore = visibleCount < (services || []).length;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [services]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount(prev => prev + PAGE_SIZE);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, visibleCount]);

  const getCategoryName = (catId) => {
    if (!catId || !categories) return "Servicio";
    const found = categories.find(c => c._id === catId);
    return found ? found.name : "Servicio";
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0E2465', mb: 0.5 }}>
        Listado de servicios
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
        Conoce la lista de servicios disponibles
      </Typography>

      {visibleServices.map((service) => (
        <Paper
          key={service._id}
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 10px 25px rgba(0,0,0,0.05)'
            }
          }}
        >
          <Box
            onClick={() => handleNavigate(service)}
            sx={{
              width: { xs: '100%', sm: '240px' },
              height: '160px',
              borderRadius: '12px',
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
              cursor: 'pointer'
            }}
          >
            <img
              src={service.img && service.img.length > 0 ? `${urlImg}/servicios/${service._id}/${service.img[0].nombre}` : 'https://via.placeholder.com/300x200'}
              alt={service.nombre}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {service.facilitadores && service.facilitadores.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: -1, // Stack avatars slightly
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  px: 1,
                  py: 0.5,
                  borderRadius: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  backdropFilter: 'blur(4px)'
                }}
              >
                {service.facilitadores.slice(0, 3).map((fac, idx) => (
                  <Avatar 
                    key={fac._id}
                    src={`${urlImg}/w_40,h_40,c_fill/v${fac.versionAvatar}/users/${fac._id}/${fac.avatar}`}
                    sx={{ 
                      width: 18, 
                      height: 18, 
                      border: '1px solid white',
                      marginLeft: idx > 0 ? '-6px' : 0
                    }}
                  />
                ))}
                {service.facilitadores.length > 3 && (
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', ml: 0.5 }}>
                    +{service.facilitadores.length - 3}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#0E2465', fontSize: '0.65rem', ml: 1 }}>
                  {service.facilitadores.length === 1 ? service.facilitadores[0].name : `${service.facilitadores.length} Pro`}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                bgcolor: 'rgba(14, 36, 101, 0.7)',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: '20px',
                fontSize: '0.7rem',
                fontWeight: 600,
                backdropFilter: 'blur(4px)'
              }}
            >
              {getCategoryName(service.categoria)}
            </Box>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box onClick={() => handleNavigate(service)} sx={{ cursor: 'pointer' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0E2465' }}>
                  {service.nombre}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  {service.facilitadores && service.facilitadores.length > 0 
                    ? service.facilitadores.map(f => f.name).join(', ') 
                    : ownerName}
                </Typography>
              </Box>
              {service.ratingPromedio > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={service.ratingPromedio} precision={0.5} readOnly size="small" sx={{ fontSize: '0.9rem', color: '#0E2465' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0E2465' }}>
                    {service.ratingPromedio.toFixed(1)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8' }}>
                  Nuevo
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B' }}>
                <LocationOnIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">CDMX, MX</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B' }}>
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{service.duracionEstimada} {service.unidadDuracion}</Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0E2465', lineHeight: 1 }}>
                  ${service.costoServicio}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                  por {service.tipoCosto}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => handleNavigate(service)}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0E2465',
                  borderRadius: '8px',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#1e3a8a' }
                }}
              >
                Ver detalle
              </Button>
            </Box>
          </Box>
        </Paper>
      ))}

      {hasMore && (
        <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          mt: 6,
          p: '12px 24px', 
          borderRadius: '12px', 
          bgcolor: 'white', 
          border: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Stack direction="row" spacing={3} alignItems="center" sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: '#0E2465', fontWeight: 600 }}>
            No encuentras el servicio que necesitas, solicita una cotización
          </Typography>
          <Button
            variant="contained"
            onClick={handleQuote}
            disabled={isOwner}
            sx={{ 
              bgcolor: isOwner ? '#E2E8F0' : '#0E2465', 
              textTransform: 'none', 
              px: 4, 
              borderRadius: '8px',
              fontWeight: 'bold',
              '&.Mui-disabled': {
                bgcolor: '#E2E8F0',
                color: '#94A3B8'
              }
            }}
          >
            Cotizar
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar 
            src={ownerAvatar} 
            sx={{ width: 40, height: 40, border: '2px solid #F1F5F9' }} 
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1 }}>
              {ownerName}
            </Typography>
            <Typography variant="caption" sx={{ color: isOnline ? '#22C55E' : '#64748B', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, bgcolor: isOnline ? '#22C55E' : '#94A3B8', borderRadius: '50%' }} />
              {isOnline ? 'Online' : 'Activo'}
            </Typography>
          </Box>
          <InfoOutlinedIcon sx={{ color: '#94A3B8', fontSize: 20, ml: 1 }} />
        </Stack>
      </Paper>
    </Box>
  );
};

export default PageServiceList;
